import { Autocomplete, Checkbox, FormControlLabel, FormGroup, TextField } from "@mui/material";
import styles from "../contactlists.module.css";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import axios from 'axios';
import { toast } from 'react-smart-toaster';
import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import NumberFormat from "react-number-format";

const phoneRegExp = /^\(?([0-9]{3})\)?[ ]?([0-9]{3})[-]?([0-9]{4})$/;
const schema = yup.object().shape({
    first_name: yup.string().max(50, 'First name cannot be more than 50 characters'),
    last_name: yup.string().max(50, 'Last name cannot be more than 50 characters'),
    email: yup.string().required('This field is Required').email('Invalid E-mail').max(50, 'Email cannot be more than 50 characters'),
    phone: yup.string().test('is-valid-phone', 'Invalid phone number', function (value) {
        if (!value || value.trim() === '') return true;
        return phoneRegExp.test(value);
    }),
    company_name: yup.string(),
    group_name: yup.string(),
    other: yup.string().when('interest_other', {
        is: true,
        then: (schema) => schema.required('This field is Required.'),
        otherwise: (schema) => schema.nullable(),
    })
});

const predefinedOptions = ["Weston Hall Ribbon Cutting", "DLD 'Cheat Codes' for development", "Propertydek Property Management System (propertydek.com)"];

function PhoneField(props) {
    return (
        <NumberFormat
            prefix=""
            displayType="input"
            type="tel"
            format={"(###) ###-####"}
            {...props}
        />
    );
}

const UserAdd = ({ setAddModalOpen, setLoading, setSnackbarMsg, setSnackbarOpen, setAllData, editData }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [groupList, setGroupList] = useState([]);
    const [selectedInterrest, setSelectedInterrest] = useState([]);

    const { handleSubmit, control, errors, setValue } = useForm({
        resolver: yupResolver(schema), mode: 'onChange',
        defaultValues: {
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
            company_name: '',
            group_name: '',
            other: ''
        }
    });

    useEffect(() => {
        async function fetchData() {
            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
            await axios.get(process.env.REACT_APP_USER_API_URL + "v1/contact-group-list", {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            }).then((response) => {
                setGroupList(response?.data?.data);
            }).catch((error) => {
                if (typeof error.response !== "undefined")
                    toast.error(error.response.data.message);
            });
        }

        fetchData();

    }, [getAccessTokenSilently]);

    useEffect(() => {
        if (editData) {
            setValue('first_name', editData?.first_name);
            setValue('last_name', editData?.last_name);
            setValue('email', editData?.email);
            setValue('phone', editData?.phone);
            setValue('company_name', editData?.company_name);
            setValue('group_name', editData?.group_name);

            if (editData?.interest_arr.length > 0) {
                let isOther = '';
                let selectedInterrestTemp = editData?.interest_arr?.map(i => {
                    if (predefinedOptions.indexOf(i) > -1) {
                        return i;
                    } else {
                        isOther = i;
                        return "Other";
                    }
                });

                setSelectedInterrest(selectedInterrestTemp);
                if (isOther !== '') {
                    setTimeout(() => {
                        setValue('other', isOther);
                    }, 200);
                }
            }
        }
    }, [editData, setValue]);

    const onSubmit = async (data) => {
        delete data.interest_other;
        data = { ...data, interests: selectedInterrest };

        setLoading(true);
        setAddModalOpen(false);

        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        return axios.post(process.env.REACT_APP_USER_API_URL + 'v1/contact-lists', data, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }).then(response => {
            setSnackbarOpen(true);
            setSnackbarMsg('Contact added successfully.');

            setLoading(false);

            setAllData(prev => {
                return [response.data.data].concat(prev);
            });
        }).catch(error => {
            if (typeof error.response !== 'undefined')
                toast.error(error.response.data.message);
            setLoading(false);
        });
    }

    const onUpdate = async (data) => {
        delete data.interest_other;
        data = { ...data, interests: selectedInterrest };

        setLoading(true);
        setAddModalOpen(false);

        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        return axios.put(process.env.REACT_APP_USER_API_URL + 'v1/contact-lists/' + editData?.id, data, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }).then(response => {
            setSnackbarOpen(true);
            setSnackbarMsg('Contact updated successfully.');

            setLoading(false);

            setAllData(prev => {
                return prev?.map(item => {
                    if (item.id === editData?.id)
                        return response.data.data;
                    return item;
                })
            });
        }).catch(error => {
            if (typeof error.response !== 'undefined')
                toast.error(error.response.data.message);
            setLoading(false);
        });
    }

    return (<div className={`${styles.AddPropertyRow}`}>
        <form onSubmit={handleSubmit(editData ? onUpdate : onSubmit)}>
            <div className={`${styles.AddPropertyHalfSec}`}>
                <div className={`${styles.AddPropertyHalfSecRow}`}>
                    <div className={`${styles.HalfSecHalf} FormGroup`}>
                        <Controller
                            name={`first_name`}
                            control={control}
                            render={(field) => (
                                <TextField
                                    onChange={(e) => field.onChange(e)}
                                    id="outlined-basic"
                                    label="First Name"
                                    variant="outlined"
                                    value={field.value}
                                />
                            )}
                        />
                        {errors.first_name && (<p className={`${styles.ErrorM}`}>{errors?.first_name?.message}</p>)}
                    </div>
                    <div className={`${styles.HalfSecHalf} FormGroup`}>
                        <Controller
                            name={`last_name`}
                            control={control}
                            render={(field) => (
                                <TextField
                                    onChange={(e) => field.onChange(e)}
                                    id="outlined-basic"
                                    label="Last Name"
                                    variant="outlined"
                                    value={field.value}
                                />
                            )}
                        />
                        {errors.last_name && (<p className={`${styles.ErrorM}`}>{errors?.last_name?.message}</p>)}
                    </div>
                    <div className={`${styles.HalfSecHalf} FormGroup`}>
                        <Controller
                            name={`email`}
                            control={control}
                            render={(field) => (
                                <TextField
                                    onChange={(e) => field.onChange(e)}
                                    id="outlined-basic"
                                    label="Email*"
                                    variant="outlined"
                                    value={field.value}
                                />
                            )}
                        />
                        {errors.email && (<p className={`${styles.ErrorM}`}>{errors?.email?.message}</p>)}
                    </div>
                    <div className={`${styles.HalfSecHalf} FormGroup`}>
                        <Controller
                            name={`phone`}
                            control={control}
                            render={(field) => (
                                <TextField
                                    onChange={(e) => field.onChange(e)}
                                    id="outlined-basic"
                                    label="Phone"
                                    variant="outlined"
                                    value={field.value}
                                    InputProps={{
                                        inputComponent: PhoneField,
                                    }}
                                />
                            )}
                        />
                        {errors.phone && (<p className={`${styles.ErrorM}`}>{errors?.phone?.message}</p>)}
                    </div>
                    <div className={`${styles.HalfSecHalf} FormGroup`}>
                        <Controller
                            name={`company_name`}
                            control={control}
                            render={(field) => (
                                <TextField
                                    onChange={(e) => field.onChange(e)}
                                    id="outlined-basic"
                                    label="Company Name"
                                    variant="outlined"
                                    value={field.value}
                                />
                            )}
                        />
                        {errors.company_name && (<p className={`${styles.ErrorM}`}>{errors?.company_name?.message}</p>)}
                    </div>
                    <div className={`${styles.HalfSecHalf} FormGroup`}>
                        <Controller
                            name={`group_name`}
                            control={control}
                            render={(field) => (
                                <Autocomplete
                                    {...field}
                                    value={field?.value ? field?.value : ""}
                                    disableClearable
                                    openOnFocus
                                    freeSolo
                                    options={groupList}
                                    onChange={(event, newValue) => {
                                        field.onChange(event);
                                        setValue('group_name', newValue);
                                    }}
                                    onInputChange={(event, newInputValue) => {
                                        setValue('group_name', newInputValue);
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Email Group" variant="outlined" />}
                                />
                            )}
                        />
                        {errors.group_name && (<p className={`${styles.ErrorM}`}>{errors?.group_name?.message}</p>)}
                    </div>
                    <div className={`${styles.HalfSecFull} FormGroup PopupCheckbox`}>
                        <p className={`${styles.InteCheckTitle}`}>Interest</p>
                        <FormGroup>
                            {predefinedOptions.map((option) => (
                                <FormControlLabel
                                    key={option.value}
                                    control={
                                        <Controller
                                            name="interests"
                                            control={control}
                                            render={(field) => (
                                                <Checkbox
                                                    {...field}
                                                    checked={selectedInterrest.includes(option)}
                                                    onChange={(e) => {
                                                        field.onChange(e);

                                                        if (e.target.checked) {
                                                            setSelectedInterrest(prev => {
                                                                return prev.concat([option]);
                                                            });
                                                        } else {
                                                            setSelectedInterrest(prev => {
                                                                return prev.filter((val) => val !== option);
                                                            });
                                                        }
                                                    }}
                                                    value={option}
                                                />
                                            )}
                                        />
                                    }
                                    label={option}
                                />
                            ))}

                            <FormControlLabel
                                control={
                                    <Controller
                                        name="interest_other"
                                        control={control}
                                        render={(field) => (
                                            <Checkbox
                                                {...field}
                                                checked={selectedInterrest.includes('Other')}
                                                onChange={(e) => {
                                                    field.onChange(e.target.checked);

                                                    if (e.target.checked) {
                                                        setSelectedInterrest(prev => {
                                                            return prev.concat(['Other']);
                                                        });
                                                    } else {
                                                        setSelectedInterrest(prev => {
                                                            return prev.filter((val) => val !== 'Other');
                                                        });
                                                    }
                                                }}
                                                value={"Other"}
                                            />
                                        )}
                                    />
                                }
                                label={"Other"}
                            />
                        </FormGroup>
                    </div>
                    {selectedInterrest.includes('Other') && <div className={`${styles.HalfSecHalf} FormGroup`}>
                        <Controller
                            name={`other`}
                            control={control}
                            render={(field) => (
                                <TextField
                                    onChange={(e) => field.onChange(e)}
                                    id="outlined-basic"
                                    label="Other Text*"
                                    variant="outlined"
                                    value={field.value}
                                />
                            )}
                        />
                        {errors.other && (<p className={`${styles.ErrorM}`}>{errors?.other?.message}</p>)}
                    </div>}
                </div>
            </div>
            <div className={`${styles.ButtonSec}`}>
                <button type="button" onClick={(e) => setAddModalOpen(false)} className="CancelPopupBU">Cancel</button>
                <button type="submit" className="SubmitPopupBU">{editData ? 'Update' : 'Add'}</button>
            </div>
        </form>
    </div>);
}

export default UserAdd;